import { FC, useCallback, useState } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { useFormContext } from 'react-hook-form'
import { FormElement, FormElementType, Variable, MobileForm, ContextMenuAction, MobileListItem } from '@mobileTypes'
import { TYPES_SOURCE_OPTIONS } from '@constants'
import { Box, Divider } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { useMobileGlobalVariables } from '@mobileHooks'

import { getDefaultFormFieldData } from '@mobileHelpers'
import { AvailableElementsPanel } from './AvailableElementsPanel'
import { ConfiguredFormPanel } from './ConfiguredFormPanel'
import { ElementConfig, ElementConfigDialog } from './ElementConfigDialog'
import { EmbeddedListConfigDialog } from './EmbeddedListConfigDialog'
import { ListItemConfigDialog } from './ListItemConfigDialog'

/** Applies BUTTON/SWIPE config. Command/actions removed — handled via Event Flows. SWIPE keeps backgroundColor only. */
function applyButtonOrSwipeConfig(element: FormElement, config: ElementConfig): void {
  element.command = undefined
  element.actions = undefined
  element.chooseActionJs = undefined
  if (config.type === 'SWIPE') {
    element.backgroundColor = config.backgroundColor
  }
  if (config.type === 'BUTTON') {
    element.buttonColor = config.buttonColor
  }
}

export const FormElementsConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileForm>()

  const elements = watch('elements') || []
  const bottomElements = watch('bottomElements') || []
  const variables = watch('variables') || [] // Get form's variables
  const formObjectCode = watch('objectCode') // Get form's objectCode
  const eventFlows = watch('eventFlows') || []
  const allFormElements = [...elements, ...bottomElements]

  // Get global variables
  const { variables: globalVariables, createGlobalVariable } = useMobileGlobalVariables()

  const handleCreateVariable = useCallback((variableInternalId: string, isGlobal: boolean = false, valueType: string = 'string', variableType: string = 'single') => {
    if (!variableInternalId || !variableInternalId.trim()) {
      return
    }

    const trimmed = variableInternalId.trim()

    const existingLocalVariable = variables.find((v: Variable) => v.internalId === trimmed)
    if (existingLocalVariable) {
      return
    }

    const existingGlobalVariable = globalVariables.find((v: Variable) => v.internalId === trimmed)
    if (existingGlobalVariable) {
      return
    }

    const newVariable: Omit<Variable, 'id' | 'createdAt' | 'updatedAt'> = {
      internalId: trimmed,
      variableType: variableType,
      valueType: valueType,
      defaultValue: undefined,
    }

    if (isGlobal) {
      createGlobalVariable(newVariable)
    } else {
      const localVariable: Variable = {
        ...newVariable,
        id: uuidv4(),
      }
      const updatedVariables = [...variables, localVariable]
      setValue('variables', updatedVariables, { shouldDirty: true })
    }
  }, [variables, globalVariables, setValue, createGlobalVariable])

  const [configDialogState, setConfigDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    elementType?: FormElementType
    destination?: { droppableId: string; index: number }
    editIndex?: number
    isBottom?: boolean
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  // State for direct EmbeddedList config dialog
  const [embeddedListDialogState, setEmbeddedListDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    destination?: { droppableId: string; index: number }
    editIndex?: number
    isBottom?: boolean
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  // State for direct ListItem config dialog
  const [listItemDialogState, setListItemDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    destination?: { droppableId: string; index: number }
    editIndex?: number
    isBottom?: boolean
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result

      if (!destination) return

      // Check if dropped back to the same place
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return
      }

      // Добавление нового элемента из Available Elements
      if (draggableId.startsWith('NEW:')) {
        const elementType = draggableId.split(':')[1] as FormElementType

        // For DIVIDER, create it directly without config dialog
        if (elementType === 'DIVIDER') {
          const newElement: FormElement = {
            type: 'DIVIDER',
            order: destination.index,
            size: 12,
            title: '',
            textAlign: 'LEFT',
            labelPosition: 'TOP',
            editable: false,
            titleHidden: true,
          }

          const isBottom = destination.droppableId === 'BOTTOM_ELEMENTS'
          const targetList = isBottom ? bottomElements : elements
          const updatedList = [...targetList]
          updatedList.splice(destination.index, 0, newElement)

          const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
          setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })

          return
        }

        // For EMBEDDED_LIST, open the EmbeddedListConfigDialog directly
        if (elementType === 'EMBEDDED_LIST') {
          setEmbeddedListDialogState({
            open: true,
            mode: 'create',
            destination,
          })
          return
        }

        // For LIST_ITEM, open the ListItemConfigDialog directly
        if (elementType === 'LIST_ITEM') {
          setListItemDialogState({
            open: true,
            mode: 'create',
            destination,
          })
          return
        }

        // Prepare initial data with objectCode for FIELD elements
        const initialData: Partial<FormElement> = {}
        if (elementType === 'FIELD' && formObjectCode) {
          initialData.data = {
            objectCode: formObjectCode,
          } as any
        }

        setConfigDialogState({
          open: true,
          mode: 'create',
          elementType,
          destination,
          initialData: Object.keys(initialData).length > 0 ? initialData : undefined,
        })

        return
      }

      // Reordering within MAIN_ELEMENTS
      if (source.droppableId === 'MAIN_ELEMENTS' && destination.droppableId === 'MAIN_ELEMENTS') {
        const reordered = Array.from(elements)
        const [removed] = reordered.splice(source.index, 1)
        reordered.splice(destination.index, 0, removed)

        // Update order
        const updated = reordered.map((el, idx) => ({ ...el, order: idx }))
        setValue('elements', updated, { shouldDirty: true })

        return
      }

      // Reordering within BOTTOM_ELEMENTS
      if (
        source.droppableId === 'BOTTOM_ELEMENTS' &&
        destination.droppableId === 'BOTTOM_ELEMENTS'
      ) {
        const reordered = Array.from(bottomElements)
        const [removed] = reordered.splice(source.index, 1)
        reordered.splice(destination.index, 0, removed)

        // Update order
        const updated = reordered.map((el, idx) => ({ ...el, order: idx }))
        setValue('bottomElements', updated, { shouldDirty: true })

        return
      }

      // Moving from MAIN to BOTTOM
      if (source.droppableId === 'MAIN_ELEMENTS' && destination.droppableId === 'BOTTOM_ELEMENTS') {
        const newMainElements = Array.from(elements)
        const [removed] = newMainElements.splice(source.index, 1)

        const newBottomElements = Array.from(bottomElements)
        newBottomElements.splice(destination.index, 0, removed)

        setValue(
          'elements',
          newMainElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )
        setValue(
          'bottomElements',
          newBottomElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )

        return
      }

      // Moving from BOTTOM to MAIN
      if (source.droppableId === 'BOTTOM_ELEMENTS' && destination.droppableId === 'MAIN_ELEMENTS') {
        const newBottomElements = Array.from(bottomElements)
        const [removed] = newBottomElements.splice(source.index, 1)

        const newMainElements = Array.from(elements)
        newMainElements.splice(destination.index, 0, removed)

        setValue(
          'elements',
          newMainElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )
        setValue(
          'bottomElements',
          newBottomElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )

        return
      }
    },
    [elements, bottomElements, setValue]
  )

  const handleSaveElementConfig = useCallback(
    (config: ElementConfig) => {
      if (configDialogState.mode === 'create') {
        // Create new element
        const newElement: FormElement = {
          type: config.type,
          id: config.id || undefined,
          internalId: config.internalId || undefined,
          order: configDialogState.destination!.index,
          size: 12,
          title: config.type === 'ACCORDION' ? (config.accordionTitle || '') : (config.title || ''),
          titleJs: config.titleJs || undefined,
          textAlign: config.textAlign || 'LEFT',
          labelPosition: 'TOP',
          editable: config.readOnly ? false : true,
          titleHidden: false,
          visibleIfJs: config.visibleIfJs,
        }

        // Add type-specific fields
        if (config.type === 'FIELD') {
          newElement.data = getDefaultFormFieldData({
            code: config.fieldCode || '',
            title: config.title || '',
            value: config.bindType === 'js' ? (config.valueJs || '') : (config.fieldCode || ''),
            valuePath: config.bindType === 'js' ? (config.valuePath || undefined) : undefined,
            objectCode: config.variableObjectCode || config.objectCode || '',
            variableName: config.variableName || '',
            variableObjectCode: config.variableObjectCode || '',
            valueType: config.valueType || 'string',
            bindType: config.bindType || 'field',
            pathArray: config.pathArray || null,
            formatDate: config.formatDate || null,
            type: config.fieldType || 'single',
            userRequired: config.required || false,
            editField: config.readOnly ? false : true,
            isMultiline: config.multiline || false,
            placeholderValue: config.placeholder || '',
          })
        } else if (config.type === 'BUTTON' || config.type === 'SWIPE') {
          applyButtonOrSwipeConfig(newElement, config)
        } else if (config.type === 'EMBEDDED_LIST') {
          newElement.embeddedListItemId = config.embeddedListItemId
          newElement.embeddedListConfig = config.embeddedListConfig
          newElement.selectedIdsVariable = config.selectedIdsVariable
          newElement.showCheckboxes = config.showCheckboxes !== undefined ? config.showCheckboxes : true
          newElement.itemIdField = config.itemIdField || '_id'
          newElement.contextMenuActions = config.contextMenuActions
          newElement.listHeader = config.listHeader
          newElement.listSubheader = config.listSubheader
          newElement.listSubheaderJs = config.listSubheaderJs
          newElement.selectAllEnabled = config.selectAllEnabled
          newElement.selectAllText = config.selectAllText
          newElement.variableBindings = config.variableBindings
          newElement.readOnlyIfJs = config.readOnlyIfJs
        } else if (config.type === 'ACCORDION') {
          newElement.style = config.accordionStyle
          newElement.accordionChildren = config.accordionChildren || []
        } else if (config.type === 'FILE') {
          newElement.fileTypes = config.fileTypes || []
          newElement.fileVariableName = config.fileVariableName || ''
        }

        // Add to correct list
        const isBottom = configDialogState.destination!.droppableId === 'BOTTOM_ELEMENTS'
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        updatedList.splice(configDialogState.destination!.index, 0, newElement)

        // Update order
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))

        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      } else {
        // Edit existing element
        const isBottom = configDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const element = { ...updatedList[configDialogState.editIndex!] }

        element.id = config.id || element.id
        element.internalId = config.internalId ?? element.internalId
        element.title = config.type === 'ACCORDION' ? (config.accordionTitle ?? element.title) : (config.title || '')
        element.titleJs = config.titleJs || undefined
        element.textAlign = config.textAlign || 'LEFT'
        element.editable = config.readOnly ? false : true
        element.visibleIfJs = config.visibleIfJs

        if (element.type === 'FIELD' && element.data) {
          element.data = {
            ...element.data,
            code: config.fieldCode || '',
            title: config.title || '',
            value: config.bindType === 'js' ? (config.valueJs || '') : (config.fieldCode || ''),
            valuePath: config.bindType === 'js' ? (config.valuePath || undefined) : undefined,
            objectCode: config.variableObjectCode || config.objectCode || '',
            variableName: config.variableName || '',
            variableObjectCode: config.variableObjectCode || '',
            bindType: config.bindType || 'field',
            valueType: config.valueType || 'string',
            formatDate: config.formatDate || null,
            type: config.fieldType || 'single',
            userRequired: config.required || false,
            isMultiline: config.multiline || false,
            placeholderValue: config.placeholder || '',
            editField: config.readOnly ? false : true,
            pathArray: config.pathArray || null,
          }
        } else if (element.type === 'BUTTON' || element.type === 'SWIPE') {
          applyButtonOrSwipeConfig(element, config)
        } else if (element.type === 'EMBEDDED_LIST') {
          element.embeddedListItemId = config.embeddedListItemId
          element.embeddedListConfig = config.embeddedListConfig
          element.selectedIdsVariable = config.selectedIdsVariable
          element.showCheckboxes = config.showCheckboxes !== undefined ? config.showCheckboxes : true
          element.itemIdField = config.itemIdField || '_id'
          element.contextMenuActions = config.contextMenuActions
          element.listHeader = config.listHeader
          element.listSubheader = config.listSubheader
          element.listSubheaderJs = config.listSubheaderJs
          element.selectAllEnabled = config.selectAllEnabled
          element.selectAllText = config.selectAllText
          element.variableBindings = config.variableBindings
          element.readOnlyIfJs = config.readOnlyIfJs
        } else if (element.type === 'ACCORDION') {
          element.style = config.accordionStyle
          element.accordionChildren = config.accordionChildren || []
        } else if (element.type === 'FILE') {
          element.fileTypes = config.fileTypes || []
          element.fileVariableName = config.fileVariableName || ''
        }

        updatedList[configDialogState.editIndex!] = element
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setConfigDialogState({ open: false, mode: 'create' })
    },
    [configDialogState, elements, bottomElements, setValue]
  )

  // Handler for saving embedded list config directly
  const handleSaveEmbeddedListConfig = useCallback(
    (config: {
      internalId?: string
      embeddedListItemId?: string
      embeddedListConfig?: Partial<MobileListItem>
      selectedIdsVariable?: string
      showCheckboxes?: boolean
      itemIdField?: string
      contextMenuActions?: ContextMenuAction[]
      listHeader?: string
      listSubheader?: string
      listSubheaderJs?: string
      selectAllEnabled?: boolean
      selectAllText?: string
      variableBindings?: Record<string, string>
      readOnlyIfJs?: string
    }) => {
      if (embeddedListDialogState.mode === 'create') {
        // Create new EMBEDDED_LIST element
        const newElement: FormElement = {
          type: 'EMBEDDED_LIST',
          order: embeddedListDialogState.destination!.index,
          size: 12,
          title: '',
          textAlign: 'LEFT',
          labelPosition: 'TOP',
          editable: false,
          titleHidden: true,
          internalId: config.internalId,
          id: config.internalId,
          embeddedListItemId: config.embeddedListItemId,
          embeddedListConfig: config.embeddedListConfig,
          selectedIdsVariable: config.selectedIdsVariable,
          showCheckboxes: config.showCheckboxes !== undefined ? config.showCheckboxes : true,
          itemIdField: config.itemIdField || '_id',
          contextMenuActions: config.contextMenuActions,
          variableBindings: config.variableBindings,
          listHeader: config.listHeader,
          listSubheader: config.listSubheader,
          listSubheaderJs: config.listSubheaderJs,
          selectAllEnabled: config.selectAllEnabled,
          selectAllText: config.selectAllText,
          readOnlyIfJs: config.readOnlyIfJs,
        }

        const isBottom = embeddedListDialogState.destination!.droppableId === 'BOTTOM_ELEMENTS'
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        updatedList.splice(embeddedListDialogState.destination!.index, 0, newElement)

        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      } else {
        // Edit existing EMBEDDED_LIST element
        const isBottom = embeddedListDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const element = { ...updatedList[embeddedListDialogState.editIndex!] }

        element.internalId = config.internalId
        element.id = config.internalId
        element.embeddedListItemId = config.embeddedListItemId
        element.embeddedListConfig = config.embeddedListConfig
        element.selectedIdsVariable = config.selectedIdsVariable
        element.showCheckboxes = config.showCheckboxes !== undefined ? config.showCheckboxes : true
        element.itemIdField = config.itemIdField || '_id'
        element.contextMenuActions = config.contextMenuActions
        element.listHeader = config.listHeader
        element.listSubheader = config.listSubheader
        element.listSubheaderJs = config.listSubheaderJs
        element.selectAllEnabled = config.selectAllEnabled
        element.selectAllText = config.selectAllText
        element.variableBindings = config.variableBindings
        element.readOnlyIfJs = config.readOnlyIfJs

        updatedList[embeddedListDialogState.editIndex!] = element
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setEmbeddedListDialogState({ open: false, mode: 'create' })
    },
    [embeddedListDialogState, elements, bottomElements, setValue]
  )

  // Handler for saving list item config directly
  const handleSaveListItemConfig = useCallback(
    (config: {
      internalId?: string
      listItemConfigId?: string
      listItemDataVariable?: string
    }) => {
      if (listItemDialogState.mode === 'create') {
        // Create new LIST_ITEM element
        const newElement: FormElement = {
          type: 'LIST_ITEM',
          order: listItemDialogState.destination!.index,
          size: 12,
          title: '',
          textAlign: 'LEFT',
          labelPosition: 'TOP',
          editable: false,
          titleHidden: true,
          internalId: config.internalId,
          listItemConfigId: config.listItemConfigId,
          listItemDataVariable: config.listItemDataVariable,
        }

        const isBottom = listItemDialogState.destination!.droppableId === 'BOTTOM_ELEMENTS'
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        updatedList.splice(listItemDialogState.destination!.index, 0, newElement)

        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      } else {
        // Edit existing LIST_ITEM element
        const isBottom = listItemDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const element = { ...updatedList[listItemDialogState.editIndex!] }

        element.internalId = config.internalId
        element.listItemConfigId = config.listItemConfigId
        element.listItemDataVariable = config.listItemDataVariable

        updatedList[listItemDialogState.editIndex!] = element
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setListItemDialogState({ open: false, mode: 'create' })
    },
    [listItemDialogState, elements, bottomElements, setValue]
  )

  const handleEditElement = useCallback(
    (index: number, isBottom: boolean) => {
      const targetList = isBottom ? bottomElements : elements
      const element = targetList[index]

      // For EMBEDDED_LIST, open the EmbeddedListConfigDialog directly
      if (element.type === 'EMBEDDED_LIST') {
        setEmbeddedListDialogState({
          open: true,
          mode: 'edit',
          editIndex: index,
          isBottom,
          initialData: element,
        })
        return
      }

      // For LIST_ITEM, open the ListItemConfigDialog directly
      if (element.type === 'LIST_ITEM') {
        setListItemDialogState({
          open: true,
          mode: 'edit',
          editIndex: index,
          isBottom,
          initialData: element,
        })
        return
      }

      setConfigDialogState({
        open: true,
        mode: 'edit',
        editIndex: index,
        isBottom,
        initialData: element,
      })
    },
    [elements, bottomElements]
  )

  const handleDeleteElement = useCallback(
    (index: number, isBottom: boolean) => {
      const targetList = isBottom ? bottomElements : elements
      const updatedList = targetList.filter((_, idx) => idx !== index)
      const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))

      setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
    },
    [elements, bottomElements, setValue]
  )

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
          <AvailableElementsPanel />
          <Divider orientation='vertical' flexItem />
          <ConfiguredFormPanel
            elements={elements}
            bottomElements={bottomElements}
            onEditElement={handleEditElement}
            onDeleteElement={handleDeleteElement}
          />
        </Box>
      </DragDropContext>

      {configDialogState.open && (
        <ElementConfigDialog
          open={configDialogState.open}
          mode={configDialogState.mode}
          elementType={configDialogState.elementType}
          initialData={configDialogState.initialData}
          variables={variables}
          formObjectCode={formObjectCode}
          onClose={() => setConfigDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveElementConfig}
          onCreateVariable={handleCreateVariable}
          eventFlows={eventFlows}
          onEventFlowsChange={flows => setValue('eventFlows', flows, { shouldDirty: true })}
          elementRef={
            configDialogState.mode === 'edit' && configDialogState.initialData?.id
              ? configDialogState.initialData.type === 'BUTTON'
                ? { type: 'button', buttonId: configDialogState.initialData.id }
                : configDialogState.initialData.type === 'SWIPE'
                  ? { type: 'swipe', buttonId: configDialogState.initialData.id }
                  : configDialogState.initialData.type === 'FIELD'
                    ? { type: 'field', fieldId: configDialogState.initialData.id }
                    : configDialogState.initialData.type === 'EMBEDDED_LIST'
                      ? { type: 'embedded_list', elementId: configDialogState.initialData.id }
                      : undefined
              : undefined
          }
          formElements={allFormElements}
        />
      )}

      {embeddedListDialogState.open && (
        <EmbeddedListConfigDialog
          open={embeddedListDialogState.open}
          initialData={embeddedListDialogState.initialData}
          variables={variables}
          onClose={() => setEmbeddedListDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveEmbeddedListConfig}
          onCreateVariable={handleCreateVariable}
        />
      )}

      {listItemDialogState.open && (
        <ListItemConfigDialog
          open={listItemDialogState.open}
          initialData={listItemDialogState.initialData}
          variables={variables}
          onClose={() => setListItemDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveListItemConfig}
          onCreateVariable={handleCreateVariable}
        />
      )}
    </>
  )
}
